import React from "react";
import { Link } from "react-router-dom";
import PortalLayout from "@/components/ventrix/PortalLayout";
import { useApiQuery } from "@/hooks/useApi";
import { StatusChip, ModeIcon, formatDateShort, Kpi } from "@/components/ventrix/atoms";
import { Package, AlertOctagon, Truck, CheckCircle2, ArrowRight } from "lucide-react";

export default function OpsConsole() {
  const shipsQ = useApiQuery("ops-ships", "/v1/shipments?limit=200");
  const statsQ = useApiQuery("ops-stats", "/v1/shipments/stats");
  const ships = shipsQ.isPending ? null : (shipsQ.data ?? { items: [] });
  const stats = statsQ.data ?? null;

  const items = ships?.items || [];
  // Tables show a page; KPIs come from the server-wide stats aggregate.
  const active = items.filter(s => s.status !== "DELIVERED");
  const exceptions = items.filter(s => s.status === "EXCEPTION");

  return (
    <PortalLayout section="ops">
      <div className="vtx-eyebrow">/ Ops · Console</div>
      <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter">Shipment console</h1>
      <p className="text-sm text-[#475569] mt-1">Every shipment across every account. Push milestones, flag exceptions.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Kpi label="Total active" value={stats?.active ?? active.length} Icon={Package} />
        <Kpi label="In transit" value={stats?.in_transit ?? "—"} Icon={Truck} accent="#1D4ED8" />
        <Kpi label="Exceptions" value={stats?.exceptions ?? exceptions.length} Icon={AlertOctagon} accent="#d41f26" />
        <Kpi label="Delivered" value={stats?.delivered ?? "—"} Icon={CheckCircle2} accent="#166534" />
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 vtx-card">
          <div className="flex items-center justify-between p-5 border-b border-[#A9C0E0]/40">
            <div className="vtx-eyebrow">All active shipments</div>
            <Link to="/ops/shipments" className="text-xs font-semibold text-[#0E2F76] hover:text-[#d41f26] inline-flex items-center gap-1" data-testid="ops-see-all">Full list <ArrowRight size={12}/></Link>
          </div>
          {ships === null && <div className="p-6 space-y-2">{[1,2,3,4].map(i=><div key={i} className="vtx-skeleton h-12" />)}</div>}
          {ships && (
            <div className="overflow-x-auto"><table className="vtx-table w-full">
              <thead><tr><th>Tracking</th><th>Route</th><th>Mode</th><th>Status</th><th>ETA</th></tr></thead>
              <tbody>
                {active.slice(0, 10).map(s => (
                  <tr key={s.id}>
                    <td><Link to={`/ops/shipments/${s.id}`} className="font-mono text-[#0E2F76] font-semibold hover:text-[#d41f26]">{s.tracking_number}</Link></td>
                    <td className="text-xs text-[#475569]">{s.origin} → {s.destination}</td>
                    <td><ModeIcon mode={s.mode} showLabel /></td>
                    <td><StatusChip status={s.status} /></td>
                    <td className="font-mono">{formatDateShort(s.eta)}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </div>
        <div className="vtx-card">
          <div className="p-5 border-b border-[#A9C0E0]/40 flex items-center justify-between">
            <div className="vtx-eyebrow">Exceptions queue</div>
            <span className="text-[10px] font-bold text-[#d41f26]">{exceptions.length}</span>
          </div>
          <div className="p-5 space-y-3">
            {exceptions.length === 0 && <div className="text-sm text-[#475569]">Zero exceptions. Ship it.</div>}
            {exceptions.slice(0, 6).map(s => (
              <Link key={s.id} to={`/ops/shipments/${s.id}`} className="block p-3 border border-[#d41f26]/30 bg-[#FEE2E2]/40 hover:bg-[#FEE2E2]">
                <div className="font-mono text-sm text-[#0E2F76] font-semibold">{s.tracking_number}</div>
                <div className="text-xs text-[#475569] mt-0.5">{s.origin} → {s.destination}</div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}
